/**
 * ElectronFileStorageAdapter
 *
 * File storage adapter for the Electron renderer process.
 * Uses local filesystem via IPC calls to the main process.
 *
 * Implements IFileStoragePort interface for the hexagonal architecture.
 */

/**
 * Create an Electron file storage adapter
 * Uses window.electronAPI exposed by preload.js
 */
export function createElectronFileStorageAdapter() {
  // Check if running in Electron
  if (!window.electronAPI) {
    throw new Error('ElectronFileStorageAdapter: window.electronAPI not available. Are you running in Electron?')
  }

  const api = window.electronAPI

  return {
    /**
     * List files in a directory
     */
    listFiles: async (appId, path) => {
      // For Electron, we use the application's source path
      const files = await api.listDirectory(path)
      return files.map((f) => ({
        name: f.name,
        path: f.path,
        isDirectory: f.isDirectory,
        type: f.isDirectory ? 'directory' : getFileType(f.name),
      }))
    },

    /**
     * Read file contents
     */
    readFile: async (appId, filePath) => {
      return api.readFile(filePath)
    },

    /**
     * Write file contents
     */
    writeFile: async (appId, filePath, content) => {
      await api.writeFile(filePath, content)
      return { success: true }
    },

    /**
     * Delete a file
     */
    deleteFile: async (appId, filePath) => {
      // TODO: Implement delete in main process
      console.warn('[ElectronFileStorageAdapter] deleteFile not yet implemented')
      return { success: false }
    },

    /**
     * Upload files (copy to destination)
     */
    uploadFiles: async (appId, files, destPath) => {
      // TODO: Implement file copy in main process
      console.warn('[ElectronFileStorageAdapter] uploadFiles not yet implemented')
      return { success: false, files: [] }
    },

    /**
     * Get project files (for file tree)
     */
    getProjectFiles: async (appId, sourcePath) => {
      if (!sourcePath) {
        return []
      }

      try {
        const files = await api.listDirectory(sourcePath)
        return buildFileTree(files, sourcePath, api)
      } catch (error) {
        console.error('[ElectronFileStorageAdapter] Error getting project files:', error)
        return []
      }
    },

    /**
     * Get COBOL file with sections
     */
    getCobolFileWithSections: async (appId, filePath) => {
      try {
        const content = await api.readFile(filePath)
        const sections = parseCobolSections(content)
        const fileName = filePath.split('/').pop()

        return {
          id: filePath,
          name: fileName,
          content,
          sections,
        }
      } catch (error) {
        console.error('[ElectronFileStorageAdapter] Error reading COBOL file:', error)
        throw error
      }
    },

    /**
     * Get Java skeleton (placeholder for transformation output)
     */
    getJavaSkeleton: async (appId) => {
      return `// Generated Java code will appear here
// Run transformation workflow to generate Java from COBOL

public class TransformedCode {
    // TODO: Transform COBOL to Java
}
`
    },

    /**
     * Select a directory using native dialog
     */
    selectDirectory: async () => {
      return api.selectDirectory()
    },

    /**
     * Select files using native dialog
     */
    selectFiles: async (options) => {
      return api.selectFiles(options)
    },
  }
}

/**
 * Get file type from filename extension
 */
function getFileType(filename) {
  const ext = filename.toLowerCase().split('.').pop()
  const typeMap = {
    cbl: 'cobol',
    cob: 'cobol',
    cpy: 'copybook',
    jcl: 'jcl',
    java: 'java',
    js: 'javascript',
    ts: 'typescript',
    json: 'json',
    xml: 'xml',
    sql: 'sql',
    txt: 'text',
    md: 'markdown',
  }
  return typeMap[ext] || 'unknown'
}

/**
 * Build file tree structure recursively
 */
async function buildFileTree(entries, basePath, api, depth = 0) {
  if (depth > 3) {
    // Limit depth to prevent huge trees
    return entries.map((e) => ({
      id: e.path,
      name: e.name,
      type: e.isDirectory ? 'directory' : getFileType(e.name),
      path: e.path,
      children: e.isDirectory ? [] : undefined,
    }))
  }

  const tree = []
  for (const entry of entries) {
    const node = {
      id: entry.path,
      name: entry.name,
      type: entry.isDirectory ? 'directory' : getFileType(entry.name),
      path: entry.path,
    }

    if (entry.isDirectory) {
      try {
        const children = await api.listDirectory(entry.path)
        node.children = await buildFileTree(children, entry.path, api, depth + 1)
      } catch {
        node.children = []
      }
    }

    tree.push(node)
  }

  return tree
}

/**
 * Parse COBOL sections from content
 */
function parseCobolSections(content) {
  const sections = []
  const lines = content.split('\n')
  let currentSection = null
  let sectionStart = 0

  const divisionPattern = /^\s{6}\s*(IDENTIFICATION|ENVIRONMENT|DATA|PROCEDURE)\s+DIVISION/i
  const sectionPattern = /^\s{6}\s*(\w[\w-]*)\s+SECTION\s*\./i

  lines.forEach((line, index) => {
    const divMatch = line.match(divisionPattern)
    const secMatch = line.match(sectionPattern)

    if (divMatch || secMatch) {
      // Save previous section
      if (currentSection) {
        currentSection.endLine = index - 1
        sections.push(currentSection)
      }

      // Start new section
      const name = divMatch ? `${divMatch[1]} DIVISION` : secMatch[1]
      currentSection = {
        id: `section-${sections.length}`,
        name,
        type: divMatch ? 'division' : 'section',
        startLine: index,
        endLine: null,
      }
      sectionStart = index
    }
  })

  // Add final section
  if (currentSection) {
    currentSection.endLine = lines.length - 1
    sections.push(currentSection)
  }

  return sections
}
